import {
  IconExclamationCircleFilled,
  IconHelpOctagonFilled,
  IconInfoCircleFilled,
} from '@tabler/icons-react';
import clsx from 'clsx';
import React, { CSSProperties, ReactNode } from 'react';

import Select, { GroupBase, OptionProps, SingleValueProps, components } from 'react-select';

import { DiligenceStatus } from '__generated__/globalTypes';
export type LoanDiligenceStatus = DiligenceStatus;

interface StatusIconMap {
  [key: string]: ReactNode;
}

const statusIconMap: StatusIconMap = {
  PendingDocuments: <IconHelpOctagonFilled color="#F2994A" />,
  Incomplete: <IconInfoCircleFilled color="#8B98F9" />,
  Complete: <IconInfoCircleFilled color="#8B98F9" />,
  Rejected: <IconExclamationCircleFilled color="#EB5757" />,
};

interface LoanStatusLabelProps {
  status: LoanDiligenceStatus;
}

const LoanStatusLabel: React.FC<LoanStatusLabelProps> = ({ status, ...props }) => {
  return (
    // items-end seems to be the only way to get the icon to align with the text
    <span {...props} className="inline-flex items-end gap-2">
      {statusIconMap[status]}
      {status}
    </span>
  );
};

export { LoanStatusLabel };

interface LoanStatusDropdownProps {
  value: LoanDiligenceStatus;
  onChange?: (value: LoanDiligenceStatus) => void;
  isInSelectedRow?: boolean;
  borderColor?: string;
}

export const LoanStatusDropdown = ({
  isInSelectedRow,
  onChange,
  value,
  borderColor,
}: LoanStatusDropdownProps) => {
  return (
    <span
      onClick={(e) => e.stopPropagation()}
      className="font-heebo text-sm"
      // borderColor is an arbitrary runtime value, so it reaches the control through a CSS variable
      style={borderColor ? ({ '--loan-status-border': borderColor } as CSSProperties) : undefined}
    >
      <Select
        unstyled
        isMulti={false}
        isSearchable={false}
        isClearable={false}
        menuPortalTarget={document.body}
        menuPosition={'fixed'}
        options={options}
        value={options.find((option) => option.value === value)}
        onChange={(option) => {
          onChange?.((option as LoanStatusOptionType).value);
        }}
        components={{
          Option: CustomOption,
          SingleValue: CustomSingleValue,
        }}
        classNames={{
          control: (state) =>
            clsx(
              '!min-h-30px min-w-140px rounded border border-solid px-2 py-0 text-left text-sm',
              state.hasValue ? 'text-slate-50' : 'text-slate-400',
              borderColor
                ? 'border-[color:var(--loan-status-border)]'
                : state.menuIsOpen
                  ? isInSelectedRow
                    ? 'border-white'
                    : 'border-pink-700'
                  : 'border-transparent',
              isInSelectedRow ? 'hover:border-white' : 'hover:border-pink-700',
            ),
          singleValue: () => 'leading-none',
          dropdownIndicator: () => (isInSelectedRow ? 'text-white' : 'text-accent-default'),
          menu: () =>
            'mt-0.5 overflow-hidden rounded-md border border-solid border-pink-700 bg-background-surface py-0.5 text-sm text-white shadow-select-menu',
          option: (state) =>
            clsx('px-3 py-1.5 leading-none', state.isFocused ? 'bg-pink-700' : 'bg-transparent'),
        }}
      />
    </span>
  );
};

interface LoanStatusOptionType {
  value: LoanDiligenceStatus;
  label: LoanDiligenceStatus;
}

const statuses: LoanDiligenceStatus[] = [
  DiligenceStatus.PENDING_DOCUMENTS,
  DiligenceStatus.IN_PROGRESS,
  DiligenceStatus.REVIEWED,
  DiligenceStatus.REJECTED,
];
const options: LoanStatusOptionType[] = statuses.map((status) => ({
  value: status,
  label: status,
}));

const CustomOption: React.FC<
  OptionProps<LoanStatusOptionType, boolean, GroupBase<LoanStatusOptionType>>
> = (props) => {
  return (
    <components.Option {...props}>
      <LoanStatusLabel status={props.label as LoanDiligenceStatus} />
    </components.Option>
  );
};

const CustomSingleValue: React.FC<SingleValueProps<LoanStatusOptionType>> = (props) => {
  return (
    <components.SingleValue {...props}>
      <LoanStatusLabel status={props.data.label} {...props.innerProps} />
    </components.SingleValue>
  );
};
